'use client';

/**
 * HubSpot Form Embed Component
 *
 * Loads the HubSpot forms embed script and renders a form
 * by calling hbspt.forms.create(). Preserves HubSpot's built-in
 * tracking, validation, smart fields, and attribution.
 */

import { useEffect, useId, useRef, useState } from 'react';
import {
  loadHubSpotForms,
  resolveHubSpotPortalId,
  HUBSPOT_PORTAL_ID,
  HUBSPOT_REGION,
} from '@/lib/hubspot-forms';
import { HUBSPOT_EMBEDS_DISABLED } from '@/lib/hubspot-embed-gate';

import { InertEmbedPlaceholder } from './InertEmbedPlaceholder';

interface HubSpotFormProps {
  formId: string;
  portalId?: string;
  region?: string;
  className?: string;
  /**
   * Defer the forms embed script until the container nears the viewport
   * (IntersectionObserver, 200px rootMargin — above-fold forms still load
   * immediately after hydration). Defaults to true; pass false only when a
   * form provably breaks under deferred loading. PERF CONTRACT — see
   * tests/unit/perf/perf-guards.test.ts.
   */
  lazy?: boolean;
  /** Keep the mount area empty while loading when the source design has no placeholder copy. */
  showLoading?: boolean;
}

interface HbsptForms {
  create: (config: Record<string, unknown>) => void;
}

declare global {
  interface Window {
    hbspt?: { forms: HbsptForms };
  }
}

export function HubSpotForm({
  formId,
  portalId = HUBSPOT_PORTAL_ID,
  region = HUBSPOT_REGION,
  className,
  lazy = true,
  showLoading = true,
}: HubSpotFormProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const instanceId = useId().replace(/:/g, '');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [visible, setVisible] = useState(!lazy);

  // When lazy, defer the form script until the container scrolls near the viewport.
  useEffect(() => {
    if (!lazy) return;
    if (!containerRef.current) return;
    const el = containerRef.current;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: '200px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [lazy]);

  useEffect(() => {
    if (!visible || !containerRef.current) return;

    let mounted = true;

    loadHubSpotForms()
      .then(() => {
        if (!mounted || !containerRef.current) return;

        // Clear any previous form
        containerRef.current.innerHTML = '';

        window.hbspt?.forms.create({
          portalId: resolveHubSpotPortalId(portalId),
          formId,
          region,
          target: `#hubspot-form-${instanceId}`,
        });

        setLoading(false);
      })
      .catch(() => {
        if (mounted) {
          setError(true);
          setLoading(false);
        }
      });

    return () => {
      mounted = false;
    };
  }, [visible, formId, portalId, region, instanceId]);

  // Staging gate — after the hooks so hook order stays stable. The effects
  // above bail on a null containerRef, which is what they see when the
  // placeholder renders in place of the mount div, so no script loads.
  if (HUBSPOT_EMBEDS_DISABLED) {
    return (
      <InertEmbedPlaceholder
        label="HubSpot form"
        detail={`form ${formId} · portal ${portalId}`}
        className={className}
      />
    );
  }

  if (error) {
    return (
      <div className={className} style={{ padding: '1rem', textAlign: 'center', color: '#666' }}>
        Unable to load form. Please refresh the page or{' '}
        <a href="tel:828-483-4237" style={{ color: '#ED8326' }}>call us at 828-483-4237</a>.
      </div>
    );
  }

  return (
    <div className={className}>
      {loading && showLoading && (
        <div style={{ padding: '2rem', textAlign: 'center', color: '#999' }}>
          Loading form...
        </div>
      )}
      <div ref={containerRef} id={`hubspot-form-${instanceId}`} />
    </div>
  );
}
